export const whatsappImageMimeTypes = ['image/jpeg', 'image/png'] as const;
export const whatsappVideoMimeTypes = ['video/mp4', 'video/3gpp'] as const;
export const whatsappDocumentMimeTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export const whatsappMediaMimeTypes = [
  ...whatsappImageMimeTypes,
  ...whatsappVideoMimeTypes,
  ...whatsappDocumentMimeTypes,
] as const;

export const whatsappInboundMediaMimeTypes = [
  'audio/aac',
  'audio/amr',
  'audio/mpeg',
  'audio/mp4',
  'audio/ogg',
  'audio/ogg; codecs=opus',
  'image/webp',
] as const;
export const whatsappStoredMediaMimeTypes = [
  ...whatsappMediaMimeTypes,
  ...whatsappInboundMediaMimeTypes,
] as const;

export type WhatsAppImageMimeType = (typeof whatsappImageMimeTypes)[number];
export type WhatsAppVideoMimeType = (typeof whatsappVideoMimeTypes)[number];
export type WhatsAppDocumentMimeType = (typeof whatsappDocumentMimeTypes)[number];
export type WhatsAppMediaMimeType = (typeof whatsappMediaMimeTypes)[number];
export type WhatsAppMediaMessageType = 'image' | 'video' | 'document';
export type WhatsAppInboundMediaMimeType = (typeof whatsappInboundMediaMimeTypes)[number];
export type WhatsAppStoredMediaMimeType = WhatsAppMediaMimeType | WhatsAppInboundMediaMimeType;

export const WHATSAPP_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const WHATSAPP_VIDEO_MAX_BYTES = 16 * 1024 * 1024;
export const WHATSAPP_DOCUMENT_MAX_BYTES = 100 * 1024 * 1024;
export const WHATSAPP_MEDIA_CAPTION_MAX_LENGTH = 1024;
export const WHATSAPP_MEDIA_UPLOAD_URL_TTL_SECONDS = 5 * 60;

export const whatsappMediaExtensions: Record<WhatsAppMediaMimeType, readonly string[]> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'video/mp4': ['mp4'],
  'video/3gpp': ['3gp', '3gpp'],
  'application/pdf': ['pdf'],
  'application/msword': ['doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
  'application/vnd.ms-excel': ['xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
};

export const whatsappImageExtensions: Record<WhatsAppImageMimeType, 'jpg' | 'png'> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

export function isWhatsAppImageMimeType(value: unknown): value is WhatsAppImageMimeType {
  return (
    typeof value === 'string' && whatsappImageMimeTypes.includes(value as WhatsAppImageMimeType)
  );
}

export function isWhatsAppVideoMimeType(value: unknown): value is WhatsAppVideoMimeType {
  return (
    typeof value === 'string' && whatsappVideoMimeTypes.includes(value as WhatsAppVideoMimeType)
  );
}

export function isWhatsAppDocumentMimeType(value: unknown): value is WhatsAppDocumentMimeType {
  return (
    typeof value === 'string' &&
    whatsappDocumentMimeTypes.includes(value as WhatsAppDocumentMimeType)
  );
}

export function isWhatsAppMediaMimeType(value: unknown): value is WhatsAppMediaMimeType {
  return (
    typeof value === 'string' && whatsappMediaMimeTypes.includes(value as WhatsAppMediaMimeType)
  );
}

export function isWhatsAppInboundMediaMimeType(
  value: unknown
): value is WhatsAppInboundMediaMimeType {
  return (
    typeof value === 'string' &&
    whatsappInboundMediaMimeTypes.includes(value as WhatsAppInboundMediaMimeType)
  );
}

export function isWhatsAppStoredMediaMimeType(
  value: unknown
): value is WhatsAppStoredMediaMimeType {
  return isWhatsAppMediaMimeType(value) || isWhatsAppInboundMediaMimeType(value);
}

export function getWhatsAppMediaMessageType(
  mimeType: WhatsAppMediaMimeType
): WhatsAppMediaMessageType {
  if (isWhatsAppImageMimeType(mimeType)) {
    return 'image';
  }

  if (isWhatsAppVideoMimeType(mimeType)) {
    return 'video';
  }

  return 'document';
}

export function getWhatsAppMediaMaxBytes(mimeType: WhatsAppMediaMimeType) {
  const messageType = getWhatsAppMediaMessageType(mimeType);

  if (messageType === 'image') {
    return WHATSAPP_IMAGE_MAX_BYTES;
  }

  if (messageType === 'video') {
    return WHATSAPP_VIDEO_MAX_BYTES;
  }

  return WHATSAPP_DOCUMENT_MAX_BYTES;
}

export function resolveWhatsAppMediaMimeType(
  mimeType: string | null | undefined,
  fileName: string
): WhatsAppMediaMimeType | null {
  if (isWhatsAppMediaMimeType(mimeType)) {
    return mimeType;
  }

  const extension = fileName.trim().toLowerCase().split('.').pop();

  if (!extension) {
    return null;
  }

  return (
    whatsappMediaMimeTypes.find(candidate =>
      whatsappMediaExtensions[candidate].includes(extension)
    ) ?? null
  );
}
